import { ConnectedWalletInfo } from "@reown/appkit";
import { BrowserProvider, Contract, Eip1193Provider } from "ethers";
import { atom } from "jotai";

import {
  GATA_ADDRESS,
  GATA_WALLET_ADDRESS,
  QUOTER_ADDRESS,
  ROUTER_ADDRESS,
  USDT_ADDRESS,
} from "@/consts";

export const walletInfoAtom = atom<ConnectedWalletInfo | undefined>(undefined);

export const walletProviderAtom = atom<Eip1193Provider | undefined>(undefined);

export const signerAtom = atom(async (get) => {
  const provider = get(walletProviderAtom);
  if (!provider) return null;
  const ethersProvider = new BrowserProvider(provider);
  const signer = await ethersProvider.getSigner();
  return signer;
});

export const gataContractAtom = atom(async (get) => {
  const signer = await get(signerAtom);
  if (!signer) return;
  const contract = new Contract(
    GATA_ADDRESS,
    [
      "function hasRole(bytes32 role, address account) view returns (bool)",
      "function asset() view returns (address)",
      "function balanceOf(address owner) view returns (uint256)",
      "function exec(address app, bytes calldata data, uint256 maxSpend) external returns (bytes memory)",
    ],
    signer,
  );
  return contract;
});

export const gataWalletContractAtom = atom(async (get) => {
  const signer = await get(signerAtom);
  if (!signer) return;
  const contract = new Contract(
    GATA_WALLET_ADDRESS,
    [
      "function hasRole(bytes32 role, address account) view returns (bool)",
      "function asset() view returns (address)",
      "function balanceOf(address owner) view returns (uint256)",
      "function exec(address app, bytes calldata data, uint256 maxSpend) external returns (bytes memory)",
    ],
    signer,
  );
  return contract;
});

export const usdtContractAtom = atom(async (get) => {
  const signer = await get(signerAtom);
  if (!signer) return;
  const contract = new Contract(
    USDT_ADDRESS,
    [
      "function approve(address spender, uint256 amount) external returns (bool)",
      "function decimals() view returns (uint8)",
    ],
    signer,
  );
  return contract;
});

// Pancake V3 Router ABI
export const routerContractAtom = atom(async (get) => {
  const signer = await get(signerAtom);
  if (!signer) return;
  const contract = new Contract(
    ROUTER_ADDRESS,
    [
      "function exactInputSingle(tuple(address tokenIn,address tokenOut,uint24 fee,address recipient,uint256 deadline,uint256 amountIn,uint256 amountOutMinimum,uint160 sqrtPriceLimitX96)) external returns (uint256)",
    ],
    signer,
  );
  return contract;
});

// Pancake V2 Quoter ABI
export const quoterContractAtom = atom(async (get) => {
  const provider = get(walletProviderAtom);
  if (!provider) return null;
  const ethersProvider = new BrowserProvider(provider);
  const contract = new Contract(
    QUOTER_ADDRESS,
    [
      {
        inputs: [
          {
            components: [
              {
                internalType: "address",
                name: "tokenIn",
                type: "address",
              },
              {
                internalType: "address",
                name: "tokenOut",
                type: "address",
              },
              {
                internalType: "uint256",
                name: "amountIn",
                type: "uint256",
              },
              {
                internalType: "uint24",
                name: "fee",
                type: "uint24",
              },
              {
                internalType: "uint160",
                name: "sqrtPriceLimitX96",
                type: "uint160",
              },
            ],
            internalType: "struct IQuoterV2.QuoteExactInputSingleParams",
            name: "params",
            type: "tuple",
          },
        ],
        name: "quoteExactInputSingle",
        outputs: [
          {
            internalType: "uint256",
            name: "amountOut",
            type: "uint256",
          },
          {
            internalType: "uint160",
            name: "sqrtPriceX96After",
            type: "uint160",
          },
          {
            internalType: "uint32",
            name: "initializedTicksCrossed",
            type: "uint32",
          },
          {
            internalType: "uint256",
            name: "gasEstimate",
            type: "uint256",
          },
        ],
        stateMutability: "nonpayable",
        type: "function",
      },
    ],
    ethersProvider,
  );
  return contract;
});
